import { AuthService } from "@/services/auth";
import { TokensType } from "@/types/auth";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { LMSAuthService } from "@/services/lms_auth";

type AuthContextType = {
    authenticated: boolean;
    loading: boolean;
    signin: (username: string, password: string) => void;
    signout: () => void;
    refresh: () => void;
    withAuth: (fn: VoidFunction) => void;
    viewAsActive: boolean;
    activateViewAs: (tokens: TokensType, onViewAsChange?: () => void) => void;
    deactivateViewAs: (onViewAsChange?: () => void) => void;
};

const AuthContext = createContext<AuthContextType>(null!);

export function AuthProvider({ children }: { children: JSX.Element }) {
    const refreshInterval = useRef<ReturnType<typeof setInterval>>();

    const [authenticated, setAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [viewAsActive, setViewAsActive] = useState(AuthService.is_view_as_active());

    useEffect(() => {
        clearInterval(refreshInterval.current);
        refreshInterval.current = setInterval(
            () => {
                refresh();
            },
            10 * 60 * 1000
        );
        return () => {
            clearInterval(refreshInterval.current);
        };
    }, []);

    useEffect(() => {
        if (!AuthService.has_refresh_expires()) {
            refresh().then(() => {
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, []);

    // Timer pour surveiller l'expiration du token view as
    useEffect(() => {
        if (!viewAsActive) return;
        const tokens = AuthService.get_view_as_tokens();
        if (!tokens || !tokens.access_expires) return;
        const now = Math.floor(Date.now() / 1000);
        const expiresIn = tokens.access_expires - now;
        if (expiresIn <= 0) {
            deactivateViewAs();
            return;
        }
        const timeout = setTimeout(() => {
            deactivateViewAs();
        }, expiresIn * 1000);
        return () => clearTimeout(timeout);
    }, [viewAsActive]);

    const signin = async (username: string, password: string) => {
        return AuthService.signin(username, password).then(() => {
            setAuthenticated(true);
        });
    };

    const signout = async () => {
        return AuthService.signout().then(() => {
            localStorage.removeItem("tokens");
            setAuthenticated(false);
        });
    };

    const refresh = async () => {
        return AuthService.refresh()
            .then(() => {
                setAuthenticated(true);
            })
            .catch(err => {
                console.error(err);
                setAuthenticated(false);
            });
    };

    const withAuth = async (fn: () => void) => {
        if (AuthService.has_access_expires()) {
            return refresh().then(() => fn());
        } else {
            return fn();
        }
    };

    const activateViewAs = (tokens: TokensType, onViewAsChange?: () => void) => {
        AuthService.set_view_as_tokens(tokens);
        setViewAsActive(true);
        // On met à jour l'utilisateur imité
        if (onViewAsChange) onViewAsChange();
    };

    const deactivateViewAs = (onViewAsChange?: () => void) => {
        // Restaurer l'état précédent du menu
        AuthService.remove_view_as_tokens();
        // Nettoyer aussi les tokens LMS view_as
        LMSAuthService.remove_view_as_tokens();
        setViewAsActive(false);
        if (onViewAsChange) onViewAsChange();
    };

    const value = { authenticated, loading, signin, signout, refresh, withAuth, viewAsActive, activateViewAs, deactivateViewAs };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
    return useContext(AuthContext);
}
